import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Group } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconMessageCircle } from '@tabler/icons-react';
import { chatService } from '@/api/services/ChatService';
import { roomIdToParam } from '@/chat/roomId';
import { ROUTES } from '@/routes/paths';
import type { AgentRoomType } from '@/types/chat';
import type { BargainStatus, BargainUserRole } from '@/types/deals';

interface DealChatButtonProps {
  readonly bargainId: number;
  readonly userRole?: BargainUserRole;
  readonly status?: BargainStatus;
}

// CTA «Чат …» на странице сделки. Чат только после старта (IN_PROGRESS) и строго через агента:
// покупатель/продавец → одна кнопка «Чат с агентом» (roomType бэкенд выводит из роли);
// агент → две кнопки «Чат с покупателем/продавцом» (явный roomType). BANK/прочие — чата нет.
export default function DealChatButton({ bargainId, userRole, status }: DealChatButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const open = useMutation({
    mutationFn: (roomType?: AgentRoomType) => chatService.getOrCreateAgentRoom(bargainId, { roomType }),
    onSuccess: (roomId) => navigate(`${ROUTES.APP_CHAT}?room=${encodeURIComponent(roomIdToParam(roomId))}`),
    // 400/404: не участник / нет агента / сделка не в работе — объясняем, остаёмся на странице.
    onError: () => notifications.show({ color: 'red', message: t('app.chat.unavailable') }),
  });

  if (status !== 'IN_PROGRESS') {
    return null;
  }

  if (userRole === 'AGENT') {
    return (
      <Group gap="sm">
        <Button
          variant="default"
          size="compact-sm"
          leftSection={<IconMessageCircle size={16} />}
          loading={open.isPending && open.variables === 'AGENT_AND_BUYER'}
          disabled={open.isPending && open.variables !== 'AGENT_AND_BUYER'}
          onClick={() => open.mutate('AGENT_AND_BUYER')}
        >
          {t('app.chat.withBuyer')}
        </Button>
        <Button
          variant="default"
          size="compact-sm"
          leftSection={<IconMessageCircle size={16} />}
          loading={open.isPending && open.variables === 'AGENT_AND_SELLER'}
          disabled={open.isPending && open.variables !== 'AGENT_AND_SELLER'}
          onClick={() => open.mutate('AGENT_AND_SELLER')}
        >
          {t('app.chat.withSeller')}
        </Button>
      </Group>
    );
  }

  if (userRole === 'CUSTOMER' || userRole === 'SELLER') {
    return (
      <Button
        variant="default"
        size="compact-sm"
        leftSection={<IconMessageCircle size={16} />}
        loading={open.isPending}
        onClick={() => open.mutate(undefined)}
      >
        {t('app.chat.withAgent')}
      </Button>
    );
  }

  return null;
}
